import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Collapse } from '@mui/material';
import { matchIsValidTel } from 'mui-tel-input';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { PhoneInput } from 'src/components/PhoneInput';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import {
  hideAppDownloadBannerPreference,
  sendAppDownloadLink,
} from 'src/models/platform/platform';
import toast from 'src/utils/toast';

export const DownloadAppBanner = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'signup' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const { session } = useSession();
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>();
  const [isVisible, setIsVisible] = useState(true);

  const sendLinkMutation = useMutation(sendAppDownloadLink, {
    onSuccess: () => {
      toast.success(tCommon('messages.downloadLinksSent'));
      setPhoneNumber(undefined);
      setIsVisible(false);
    },
    onError: () => {
      toast.error(tCommon('messages.failedToSendDownloadLinks'));
    },
  });

  const hideBannerMutation = useMutation(hideAppDownloadBannerPreference, {
    onError: (error) => {
      /* eslint-disable-next-line no-console */
      console.error('Failed to hide banner:', error);
    },
  });

  const handleSendLinks = () => {
    if (!phoneNumber || !matchIsValidTel(phoneNumber)) {
      toast.error(tCommon('messages.enterValidPhoneNumber'));
      return;
    }

    const countryCodeForAPI = session?.country || 'US';

    sendLinkMutation.mutate({
      phoneNumber: phoneNumber,
      countryCode: countryCodeForAPI,
    });
  };

  const handleClose = () => {
    setIsVisible(false);

    hideBannerMutation.mutate();
  };

  const brandName = session?.brand || 'Exercise.com';

  const defaultCountryCode = session?.country || 'US';

  return (
    <Collapse in={isVisible}>
      <Box
        id="download-app-banner"
        sx={{
          position: 'relative',
          backgroundColor: 'primary.light',
          color: 'primary.contrastText',
          py: 1,
          px: { xs: 2, sm: 6 },
          pr: { xs: 6, sm: 6 },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          px={2}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Icon
              size="large"
              color="inherit"
              icon={icon({ name: 'mobile-screen', style: 'solid' })}
            />
            <Typography variant="body2">
              {tCommon('messages.logWorkoutAnywhere', { brandName })}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <PhoneInput
              placeholder={tCommon('labels.phoneNumber')}
              value={phoneNumber}
              onChange={(value) => {
                const cleanValue = value.replace(/\s/g, '');
                setPhoneNumber(cleanValue);
              }}
              defaultCountry={defaultCountryCode as any}
              sx={{ backgroundColor: 'background.paper', borderRadius: 1 }}
            />
            <Button
              variant="contained"
              onClick={handleSendLinks}
              disabled={sendLinkMutation.isLoading}
            >
              {sendLinkMutation.isLoading
                ? t('sending')
                : tCommon('buttons.sendLinks')}
            </Button>
          </Stack>
        </Stack>
        <IconButton
          size="medium"
          onClick={handleClose}
          sx={{
            color: 'inherit',
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          <Icon icon={icon({ name: 'close', style: 'regular' })} size="small" />
        </IconButton>
      </Box>
    </Collapse>
  );
};
